import React, { useState, useEffect } from 'react';
import { 
  Building2, Search, MapPin, Users, Award, ExternalLink,
  Filter, Globe, CheckCircle, Heart, Share2, MessageCircle,
  Lightbulb, Target, UserCheck, TrendingUp, ArrowRight, Star, BookOpen
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DirectoryPage = () => {
  const [organizations, setOrganizations] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSize, setFilterSize] = useState('all');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [showInfoCards, setShowInfoCards] = useState(true);
  
  // Check if user is logged in
  const isLoggedIn = localStorage.getItem('cness_authenticated') === 'true';
  const userRole = localStorage.getItem('cness_user_role');

  useEffect(() => {
    fetchDirectory();
    fetchIndustries();
  }, []);

  const fetchDirectory = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/directory`);
      const data = await res.json();
      setOrganizations(data.directory || []);
    } catch (err) {
      console.error('Failed to fetch directory:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchIndustries = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/industries`);
      const data = await res.json();
      setIndustries(data.industries || []);
    } catch (err) {
      console.error('Failed to fetch industries:', err);
    }
  };

  const uniqueIndustries = industries.length > 0 
    ? industries.map(i => i.name)
    : [...new Set(organizations.map(o => o.industry).filter(Boolean))];

  const filteredOrgs = organizations
    .filter(o => filterSize === 'all' || o.organization_size === filterSize)
    .filter(o => filterIndustry === 'all' || o.industry === filterIndustry)
    .filter(o => 
      o.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.industry?.toLowerCase().includes(searchQuery.toLowerCase())
    );

  // Info Cards data
  const infoCards = [
    {
      icon: Target,
      title: 'CNESS Assessment',
      description: 'Evaluate your organization across 6 pillars of conscious business. Gain actionable insights to improve your practices and earn certification.',
      benefits: ['Comprehensive evaluation', 'Clear improvement roadmap', 'Industry benchmarking'],
      cta: 'Start Assessment',
      ctaLink: '/signup',
      color: 'from-indigo-500 to-purple-500'
    },
    {
      icon: UserCheck,
      title: 'Mentorship Program',
      description: 'Connect with experienced leaders who can guide your conscious business journey. Get personalized advice and support.',
      benefits: ['1-on-1 guidance', 'Expert network access', 'Progress tracking'],
      cta: 'Find a Mentor',
      ctaLink: '/mentor-partner',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      icon: Award,
      title: 'Certification Benefits',
      description: 'Join the directory of certified organizations. Build trust with customers and partners through verified conscious practices.',
      benefits: ['Public recognition', 'Competitive advantage', 'Community access'],
      cta: 'Get Certified',
      ctaLink: '/certification',
      color: 'from-amber-500 to-orange-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-4xl md:text-5xl font-black mb-4">Certified Organizations Directory</h1>
          <p className="text-xl text-indigo-100 max-w-2xl mx-auto">
            Discover organizations that have achieved CNESS certification and demonstrated commitment to conscious business practices.
          </p>
          {!isLoggedIn && (
            <div className="mt-6">
              <a 
                href="/signup"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-indigo-600 rounded-xl font-semibold hover:bg-indigo-50 transition-all"
              >
                Join the Directory
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Information Cards Section */}
      {showInfoCards && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Why Get Certified?</h2>
            <button
              onClick={() => setShowInfoCards(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Hide
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {infoCards.map((card, idx) => (
              <InfoCard key={idx} card={card} />
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search organizations..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="search-directory"
              />
            </div>

            <select
              value={filterSize}
              onChange={(e) => setFilterSize(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="filter-size"
            >
              <option value="all">All Sizes</option>
              <option value="MICRO">Micro</option>
              <option value="SMALL">Small</option>
              <option value="MEDIUM">Medium</option>
              <option value="LARGE">Large</option>
              <option value="ENTERPRISE">Enterprise</option>
            </select>

            <select
              value={filterIndustry}
              onChange={(e) => setFilterIndustry(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="filter-industry"
            >
              <option value="all">All Industries</option>
              {uniqueIndustries.map(i => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>

            <div className="text-gray-600 text-sm">
              <span className="font-semibold">{filteredOrgs.length}</span> certified organizations
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Empty State */}
        {filteredOrgs.length === 0 && !loading && (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-600 mb-2">No Certified Organizations Yet</h3>
            <p className="text-gray-500">
              Organizations will appear here once they complete certification.
            </p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-16">
            <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading directory...</p>
          </div>
        )}

        {/* Organizations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredOrgs.map(org => (
            <OrgCard key={org.id || org.organization_id} organization={org} />
          ))}
        </div>
      </div>
    </div>
  );
};


// ============== ORGANIZATION CARD ==============
const OrgCard = ({ organization }) => {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(organization.likes || 0);

  const tierColors = {
    Luminary: 'from-amber-500 to-orange-500',
    Inspired: 'from-purple-500 to-indigo-500',
    Aspiring: 'from-green-500 to-teal-500'
  };

  const sizeLabels = {
    MICRO: '1-10 employees',
    SMALL: '11-50 employees',
    MEDIUM: '51-250 employees',
    LARGE: '251-1000 employees',
    ENTERPRISE: '1000+ employees'
  };

  const handleLike = async () => {
    setLiked(!liked);
    setLikeCount(liked ? likeCount - 1 : likeCount + 1);
    // Could call API here to persist like
  };

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/directory?org=${organization.id}`;
    if (navigator.share) {
      navigator.share({
        title: `${organization.name} - CNESS Certified`,
        text: `Check out ${organization.name}, a CNESS certified organization!`,
        url: shareUrl
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all group">
      {/* Header with Logo */}
      <div className="h-32 bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center relative">
        {organization.logo_url ? (
          <img 
            src={organization.logo_url} 
            alt={organization.name}
            className="w-20 h-20 object-contain bg-white rounded-xl p-2"
          />
        ) : (
          <div className="w-20 h-20 bg-white/20 rounded-xl flex items-center justify-center">
            <Building2 className="w-10 h-10 text-white" />
          </div>
        )}
        
        {/* Certification Badge */}
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <span className="text-xs font-semibold text-green-700">Certified</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors">
          {organization.name}
        </h3>

        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            <span>{organization.organization_size} - {sizeLabels[organization.organization_size] || ''}</span>
          </div>
          
          {organization.industry && (
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-gray-400" />
              <span>{organization.industry}</span>
            </div>
          )}

          {organization.certification_tier && (
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-500" />
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold bg-gradient-to-r ${tierColors[organization.certification_tier] || 'from-gray-500 to-gray-600'} text-white`}>
                {organization.certification_tier}
              </span>
            </div>
          )}
        </div>

        {/* Certified Date */}
        {organization.certified_at && (
          <p className="text-xs text-gray-400 mt-4">
            Certified: {new Date(organization.certified_at).toLocaleDateString()}
          </p>
        )}

        {/* Engagement Actions - Available to all users */}
        <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100">
          <button 
            onClick={handleLike}
            className="flex items-center gap-1 text-gray-500 hover:text-red-500 transition-colors"
            data-testid={`like-${organization.id}`}
          >
            <Heart className={`w-4 h-4 ${liked ? 'fill-red-500 text-red-500' : ''}`} />
            <span className="text-xs">{likeCount}</span>
          </button>
          
          <button 
            onClick={handleShare}
            className="flex items-center gap-1 text-gray-500 hover:text-indigo-500 transition-colors"
            data-testid={`share-${organization.id}`}
          >
            <Share2 className="w-4 h-4" />
            <span className="text-xs">Share</span>
          </button>
          
          <a 
            href={organization.website || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-gray-500 hover:text-green-500 transition-colors ml-auto"
          >
            <ExternalLink className="w-4 h-4" />
            <span className="text-xs">Visit</span>
          </a>
        </div>
      </div>
    </div>
  );
};


// ============== INFO CARD ==============
const InfoCard = ({ card }) => {
  const Icon = card.icon;
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all">
      <div className={`h-2 bg-gradient-to-r ${card.color}`}></div>
      <div className="p-6">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${card.color} flex items-center justify-center mb-4`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">{card.title}</h3>
        <p className="text-gray-600 text-sm mb-4">{card.description}</p>
        
        <ul className="space-y-2 mb-4">
          {card.benefits.map((benefit, idx) => (
            <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500" />
              {benefit}
            </li>
          ))}
        </ul>
        
        <a
          href={card.ctaLink}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium bg-gradient-to-r ${card.color} hover:opacity-90 transition-opacity`}
        >
          {card.cta}
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

export default DirectoryPage;
